import React, {
  useCallback,
  useEffect,
  useMemo,
  type ComponentType,
  type ReactNode,
} from 'react';

import { getAdminApiUrl } from '@adsk/acs-sc-web-platform-api';
import { CONTEXT_NAMES } from '@adsk/alloy-helpers';
import {
  getOrCreateGlobalContext,
  useStoreWithValidation,
} from '@adsk/alloy-react-context';
import { noop } from '@adsk/alloy-react-helpers';

import { useCurrentProjectId } from './CurrentProjectIdContext';
import {
  BaseCombinedStore,
  createPaginatedStore,
} from './helpers/createDataModificationStore';
import { warnLegacySetEnabled } from './helpers/warnLegacySetEnabled';
import { Company } from './types';

type CompaniesContextValue = {
  data: Company[];
  loading: boolean;
  error?: null | Error;
  retry(): void;
  addCompanies(companies: Company[]): void;
  updateCompany(company: Company): void;
  removeCompanyById(id: Company['id']): void;
  setEnabled?: (enabled: boolean) => void;
};

/**
 * Context for CompaniesContext Provider.
 */
const CompaniesContext = getOrCreateGlobalContext<CompaniesContextValue>(
  CONTEXT_NAMES.CompaniesContext,
);

const useCompaniesStoreWithValidation = () =>
  useStoreWithValidation<BaseCombinedStore<Company>>(
    CONTEXT_NAMES.CompaniesContext,
    {
      hook: 'useCompanies',
      provider: 'CompaniesProvider',
    },
  );

const useCompaniesStore = () =>
  createPaginatedStore<Company>({
    contextName: CONTEXT_NAMES.CompaniesContext,
  })();

const useCompaniesApiUrl = () => {
  const PAGE_LIMIT = 200;

  const projectId = useCurrentProjectId();
  const adminApiUrl = getAdminApiUrl();
  return projectId
    ? `${adminApiUrl}/v1/projects/${projectId}/companies?limit=${PAGE_LIMIT}`
    : null;
};

/** Returns the list of companies for the current project, along with loading/error state and store mutation helpers. Must be used inside `CompaniesProvider`. */
export const useCompanies = () => {
  const url = useCompaniesApiUrl();
  const {
    entities,
    error,
    loading,
    fetchPage,
    addEntities,
    updateEntity,
    removeEntityById,
    retry,
  } = useCompaniesStoreWithValidation();

  useEffect(() => {
    fetchPage(url);
  }, [url, fetchPage]);

  return {
    data: entities,
    loading,
    error,
    retry,
    addCompanies: addEntities,
    updateCompany: updateEntity,
    removeCompanyById: removeEntityById,
    setEnabled: noop,
  };
};

export const useCompaniesProvider = () => {
  const url = useCompaniesApiUrl();
  const {
    entities,
    error,
    loading,
    addEntities,
    updateEntity,
    removeEntityById,
    retry,
    fetchPage,
  } = useCompaniesStore();

  // Trigger fetch on setEnabled for legacy provider backwards compatibility
  const legacySetEnabled = useCallback(() => {
    warnLegacySetEnabled(CONTEXT_NAMES.CompaniesContext);
    fetchPage(url);
  }, [url, fetchPage]);

  return useMemo(
    () => ({
      data: entities,
      loading,
      error,
      retry,
      addCompanies: addEntities,
      updateCompany: updateEntity,
      removeCompanyById: removeEntityById,
      setEnabled: legacySetEnabled,
    }),
    [
      entities,
      loading,
      error,
      retry,
      addEntities,
      updateEntity,
      removeEntityById,
      legacySetEnabled,
    ],
  );
};

type CompaniesProviderProps = {
  children?: ReactNode;
};

export const CompaniesProvider: ComponentType<CompaniesProviderProps> = ({
  children,
}) => {
  const value = useCompaniesProvider();

  return (
    <CompaniesContext.Provider value={value}>
      {children}
    </CompaniesContext.Provider>
  );
};

CompaniesProvider.displayName = 'CompaniesProvider';
